import type React from "react";
import { clamp } from "remeda";

// ── Types ───────────────────────────────────────────────────────────────

export interface TimeFieldProps extends Omit<React.ComponentProps<"div">, "value" | "onChange"> {
  timezone?: string;
  /** Hide the inline `({timezone})` caption inside the input — for callers that render their own caption. */
  hideTimezone?: boolean;
  value?: Date | null;
  onValueChange: (value: Date | null) => void;
  invalid?: boolean;
  disabled?: boolean;
  /** Swap the clock icon for a spinner — use for async-save feedback without disabling input. */
  isSaving?: boolean;
  /** Seed dayPeriod from this reference when `value` is empty — bias AM/PM toward the surrounding shift time. */
  referenceTime?: Date | null;
  /** Replaces the trailing clock icon, e.g. with an action button. The saving spinner still takes precedence. */
  endAdornment?: React.ReactNode;
}

export type SegmentType = "hour" | "minute" | "dayPeriod";

export interface SegmentState {
  hour: number | null;
  minute: number | null;
  dayPeriod: "am" | "pm";
}

// ── Constants ───────────────────────────────────────────────────────────

export const ARIA_VALUE_MAX: Record<SegmentType, number> = { hour: 12, minute: 59, dayPeriod: 1 };
export const SEGMENT_ORDER: Array<SegmentType> = ["hour", "minute", "dayPeriod"];

// ── Segment ↔ Date conversion ───────────────────────────────────────────

export function dateToSegments(date: Date | null | undefined, referenceTime?: Date | null): SegmentState {
  if (!date) {
    let refPeriod: "am" | "pm" = "am";
    if (referenceTime) refPeriod = new Date(referenceTime).getHours() >= 12 ? "pm" : "am";
    return { hour: null, minute: null, dayPeriod: refPeriod };
  }
  const d = new Date(date);
  const h24 = d.getHours();
  return {
    hour: to12Hour(h24),
    minute: d.getMinutes(),
    dayPeriod: h24 >= 12 ? "pm" : "am",
  };
}

// Returns null if either hour or minute is cleared (partial state).
export function segmentsToDate(seg: SegmentState, reference: Date | null | undefined): Date | null {
  if (seg.hour == null || seg.minute == null) return null;
  const base = reference ? new Date(reference) : new Date();
  let h24 = seg.hour;
  if (seg.dayPeriod === "am" && h24 === 12) h24 = 0;
  else if (seg.dayPeriod === "pm" && h24 !== 12) h24 += 12;
  base.setHours(h24, seg.minute, 0, 0);
  return base;
}

function to12Hour(h24: number): number {
  if (h24 === 0) return 12;
  if (h24 > 12) return h24 - 12;
  return h24;
}

// ── Segment formatting & ARIA ───────────────────────────────────────────

export function formatSegment(type: SegmentType, seg: SegmentState): string {
  if (type === "hour") return seg.hour == null ? "––" : String(seg.hour);
  if (type === "minute") return seg.minute == null ? "––" : String(seg.minute).padStart(2, "0");
  return seg.dayPeriod;
}

export function getAriaValueNow(type: SegmentType, seg: SegmentState): number | undefined {
  if (type === "hour") return seg.hour ?? undefined;
  if (type === "minute") return seg.minute ?? undefined;
  return seg.dayPeriod === "am" ? 0 : 1;
}

// ── Keyboard handling ───────────────────────────────────────────────────

interface KeyHandlerContext {
  segments: SegmentState;
  updateSegments: (seg: SegmentState) => void;
  focusSegment: (index: number) => void;
  enteredKeys: React.RefObject<string>;
}

export function createKeyDownHandler(ctx: KeyHandlerContext) {
  return (e: React.KeyboardEvent, type: SegmentType, index: number) => {
    const { key } = e;

    if (key === "Tab") {
      ctx.enteredKeys.current = "";
      return;
    }

    if (handleNavigation(e, key, index, ctx)) return;
    if (handleStep(e, key, type, ctx)) return;
    if (handleHomeEnd(e, key, type, ctx)) return;
    if (handleDelete(e, key, type, index, ctx)) return;
    if (handleDigitInput(e, key, type, index, ctx)) return;
    handleDayPeriodInput(e, key, type, ctx);
  };
}

function handleNavigation(e: React.KeyboardEvent, key: string, index: number, ctx: KeyHandlerContext): boolean {
  if (key === "ArrowRight") {
    e.preventDefault();
    ctx.enteredKeys.current = "";
    if (index < 2) ctx.focusSegment(index + 1);
    return true;
  }
  if (key === "ArrowLeft") {
    e.preventDefault();
    ctx.enteredKeys.current = "";
    if (index > 0) ctx.focusSegment(index - 1);
    return true;
  }
  return false;
}

const PAGE_STEP: Record<SegmentType, number> = { hour: 2, minute: 15, dayPeriod: 1 };

function handleStep(e: React.KeyboardEvent, key: string, type: SegmentType, ctx: KeyHandlerContext): boolean {
  if (key !== "ArrowUp" && key !== "ArrowDown" && key !== "PageUp" && key !== "PageDown") return false;
  e.preventDefault();
  ctx.enteredKeys.current = "";
  const isPage = key === "PageUp" || key === "PageDown";
  const delta = key === "ArrowUp" || key === "PageUp" ? 1 : -1;
  const step = isPage ? PAGE_STEP[type] : 1;

  if (type === "hour") {
    const cur = ctx.segments.hour ?? 12;
    const next = ((cur - 1 + delta * step + 120) % 12) + 1;
    ctx.updateSegments({ ...ctx.segments, hour: next });
  } else if (type === "minute") {
    const cur = ctx.segments.minute ?? 0;
    const next = (cur + delta * step + 600) % 60;
    ctx.updateSegments({ ...ctx.segments, minute: next });
  } else {
    ctx.updateSegments({ ...ctx.segments, dayPeriod: ctx.segments.dayPeriod === "am" ? "pm" : "am" });
  }
  return true;
}

function handleHomeEnd(e: React.KeyboardEvent, key: string, type: SegmentType, ctx: KeyHandlerContext): boolean {
  if (key !== "Home" && key !== "End") return false;
  e.preventDefault();
  ctx.enteredKeys.current = "";
  if (type === "hour") ctx.updateSegments({ ...ctx.segments, hour: key === "Home" ? 1 : 12 });
  else if (type === "minute") ctx.updateSegments({ ...ctx.segments, minute: key === "Home" ? 0 : 59 });
  else ctx.updateSegments({ ...ctx.segments, dayPeriod: key === "Home" ? "am" : "pm" });
  return true;
}

function handleDelete(
  e: React.KeyboardEvent,
  key: string,
  type: SegmentType,
  index: number,
  ctx: KeyHandlerContext,
): boolean {
  if (key !== "Backspace" && key !== "Delete") return false;
  e.preventDefault();
  if (type === "hour") {
    if (ctx.segments.hour == null && index > 0) {
      ctx.focusSegment(index - 1);
      return true;
    }
    ctx.updateSegments({ ...ctx.segments, hour: null });
  } else if (type === "minute") {
    if (ctx.segments.minute == null && index > 0) {
      ctx.focusSegment(index - 1);
      return true;
    }
    ctx.updateSegments({ ...ctx.segments, minute: null });
  }
  ctx.enteredKeys.current = "";
  return true;
}

const DIGIT_RE = /^\d$/;

function handleDigitInput(
  e: React.KeyboardEvent,
  key: string,
  type: SegmentType,
  index: number,
  ctx: KeyHandlerContext,
): boolean {
  if (type === "dayPeriod" || !DIGIT_RE.test(key)) return false;
  e.preventDefault();
  const newKeys = ctx.enteredKeys.current + key;
  const num = Number.parseInt(newKeys, 10);

  if (type === "hour") {
    ctx.updateSegments({ ...ctx.segments, hour: clamp(num, { min: 1, max: 12 }) });
    if (newKeys.length >= 2 || num > 1) {
      ctx.enteredKeys.current = "";
      ctx.focusSegment(index + 1);
    } else {
      ctx.enteredKeys.current = newKeys;
    }
  } else {
    ctx.updateSegments({ ...ctx.segments, minute: clamp(num, { min: 0, max: 59 }) });
    if (newKeys.length >= 2 || num > 5) {
      ctx.enteredKeys.current = "";
      ctx.focusSegment(index + 1);
    } else {
      ctx.enteredKeys.current = newKeys;
    }
  }
  return true;
}

function handleDayPeriodInput(e: React.KeyboardEvent, key: string, type: SegmentType, ctx: KeyHandlerContext): boolean {
  if (type !== "dayPeriod") return false;
  const lower = key.toLowerCase();
  if (lower !== "a" && lower !== "p") return false;
  e.preventDefault();
  ctx.updateSegments({ ...ctx.segments, dayPeriod: lower === "a" ? "am" : "pm" });
  return true;
}
